import { useMutation, useQuery } from "@tanstack/react-query";
import { get, getBlob } from "./divertidogsApiUtils";
import { RespuestaBlob } from "../utils/blobUtils";

export interface AsistenciaPorDia {
  fecha: string;
  conteo: number;
}

export interface PlanesActivosPorDia {
  fecha: string;
  conteo: number;
}

export interface MascotaDesertora {
  idMascota: string;
  nombreMascota: string;
  propietario: string;
  idPropietario: string;
  fechaUltimoPlan: string;
}

export interface PlanActivoGuarderia {
  idAgendamiento: string;
  mascota: string;
  propietario: string;
  fechaAgendamiento: string;
  fechaInicio: string;
  fechaFin: string;
  numeroDias: number;
  numeroDiasPendientes: number;
  valorTotal: number;
  valorPendiente: number;
}

export interface VacunaVencida {
  idMascota: string;
  nombreMascota: string;
  propietario: string;
  idPropietario: string;
  fechaVacuna: string | null;
}

interface ParametrosDescargaAsistenciasPorDia {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaPlanesActivosPorDia {
  fechaInicio: string;
  fechaFin: string;
}

export const QUERY_KEY_ASISTENCIAS_POR_DIA = "QUERY_KEY_ASISTENCIAS_POR_DIA";
export const QUERY_KEY_PLANES_ACTIVOS_GUARDERIA_POR_DIA =
  "QUERY_KEY_PLANES_ACTIVOS_GUARDERIA_POR_DIA";
export const QUERY_KEY_MASCOTAS_DESERTORAS = "QUERY_KEY_MASCOTAS_DESERTORAS";
export const QUERY_KEY_VACUNAS_VENCIDAS = "QUERY_KEY_VACUNAS_VENCIDAS";
export const QUERY_KEY_PLANES_ACTIVOS = "QUERY_KEY_PLANES_ACTIVOS";

export const obtenerAsistenciasPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<AsistenciaPorDia[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);

  return get<AsistenciaPorDia[]>(
    `analisis/operaciones/asistencias-por-dia?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerAsistenciasPorDia = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_ASISTENCIAS_POR_DIA, fechaInicio, fechaFin],
    async () => await obtenerAsistenciasPorDia(fechaInicio, fechaFin)
  );
};

export const descargarAsistenciasPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);

  return getBlob(
    `analisis/operaciones/asistencias-por-dia/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarAsistenciasPorDia = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaAsistenciasPorDia) =>
      await descargarAsistenciasPorDia(fechaInicio, fechaFin)
  );

export const obtenerPlanesActivosGuarderiaPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<PlanesActivosPorDia[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);

  return get<PlanesActivosPorDia[]>(
    `analisis/operaciones/guarderia/asistencias-planes-por-dia?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerPlanesActivosGuarderiaPorDia = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_PLANES_ACTIVOS_GUARDERIA_POR_DIA, fechaInicio, fechaFin],
    async () => await obtenerPlanesActivosGuarderiaPorDia(fechaInicio, fechaFin)
  );
};

export const descargarPlanesActivosGuarderiaPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);

  return getBlob(
    `analisis/operaciones/guarderia/asistencias-planes-por-dia/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarPlanesActivosGuarderiaPorDia = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaPlanesActivosPorDia) =>
      await descargarPlanesActivosGuarderiaPorDia(fechaInicio, fechaFin)
  );

export const obtenerMascotasDesertoras = async (): Promise<
  MascotaDesertora[] | undefined
> => {
  return get<MascotaDesertora[]>(
    "analisis/operaciones/guarderia/mascotas-desertoras"
  );
};

export const useObtenerMascotasDesertoras = () => {
  return useQuery(
    [QUERY_KEY_MASCOTAS_DESERTORAS],
    async () => await obtenerMascotasDesertoras()
  );
};

export const descargarMascotasDesertoras = async (): Promise<RespuestaBlob> => {
  return getBlob("analisis/operaciones/guarderia/mascotas-desertoras/descargar");
};

export const useDescargarMascotasDesertoras = () =>
  useMutation(async () => await descargarMascotasDesertoras());

export const obtenerVacunasVencidas = async (): Promise<
  VacunaVencida[] | undefined
> => {
  return get<VacunaVencida[]>("analisis/operaciones/vacunas-vencidas");
};

export const useObtenerVacunasVencidas = () => {
  return useQuery(
    [QUERY_KEY_VACUNAS_VENCIDAS],
    async () => await obtenerVacunasVencidas()
  );
};

export const descargarVacunasVencidas = async (): Promise<RespuestaBlob> => {
  return getBlob("analisis/operaciones/vacunas-vencidas/descargar");
};

export const useDescargarVacunasVencidas = () =>
  useMutation(async () => await descargarVacunasVencidas());

export const obtenerPlanesActivos = async (
  fechaCorte: string
): Promise<PlanActivoGuarderia[] | undefined> => {
  const queryFechaCorte = encodeURIComponent(fechaCorte);

  return get<PlanActivoGuarderia[]>(
    `analisis/operaciones/guarderia/planes-activos?fecha-corte=${queryFechaCorte}`
  );
};

export const useObtenerPlanesActivos = (fechaCorte: string) => {
  return useQuery(
    [QUERY_KEY_PLANES_ACTIVOS, fechaCorte],
    async () => await obtenerPlanesActivos(fechaCorte)
  );
};

export const descargarPlanesActivos = async (
  fechaCorte: string
): Promise<RespuestaBlob> => {
  const queryFechaCorte = encodeURIComponent(fechaCorte);

  return getBlob(
    `analisis/operaciones/guarderia/planes-activos/descargar?fecha-corte=${queryFechaCorte}`
  );
};

export const useDescargarPlanesActivos = () =>
  useMutation(
    async ({ fechaCorte }: { fechaCorte: string }) =>
      await descargarPlanesActivos(fechaCorte)
  );
